import {
  canReply,
  canResolve,
  CORRECTION_RELEASE_MAX,
  CORRECTION_TEXT_MAX,
  INTERNAL_NOTE_MARK,
  isReplyBlockedByPriorityRule,
  PRIORITY_REQUIRED_MESSAGE,
  TICKET_MESSAGE_ERROR_CODES,
  TICKET_MESSAGE_TEXT_MAX,
  TICKET_RECIPIENT_SOURCE_LABELS,
  TICKET_STATE_LABELS,
  type PostedTicketMessage,
  type TicketDetail,
  type TicketMessageKind,
  type TicketReplyDraft,
} from '@easyhelp/shared';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useRef, useState, type FormEvent } from 'react';
import type { ApiError } from '../../api/client';
import { useResolveTicket } from '../../api/ticket-closure';
import { useMessageAttachmentLimits, usePostTicketMessage } from '../../api/ticket-messages';
import { useQueueSettings } from '../../api/queue-settings';
import { attachmentProblems } from './attachment-checks';
import { ComposerAttachments } from './ComposerAttachments';
import { DetailSection } from './DetailSection';
import { ReplyDraftBanner } from './ReplyDraftBanner';
import { declaredContactsNotice } from './notices';
import { RESOLVE_COPY, resolveBlockedReason, resolveErrorMessage, resolveOutcome } from './resolve-text';

/** Perché la risposta non è disponibile, oppure `null`. La nota interna resta sempre possibile. */
function replyBlockedReason(
  ticket: Pick<TicketDetail, 'state' | 'priority'>,
  settings: { requirePriorityBeforeReply: boolean } | undefined
): string | null {
  if (!canReply(ticket.state)) {
    return `Non si risponde a un ticket «${TICKET_STATE_LABELS[ticket.state]}»: la nota interna resta possibile.`;
  }
  // La regola di #3, con lo stesso testo dell'errore dell'API (`TicketReplyPolicy`).
  if (settings && isReplyBlockedByPriorityRule(ticket, settings)) {
    return `${PRIORITY_REQUIRED_MESSAGE} La nota interna resta possibile.`;
  }
  return null;
}

/**
 * Errori dopo i quali la stessa risposta non va riproposta: l'email è partita senza essere
 * registrata, oppure con quella bozza c'è già un invio in corso o dall'esito non registrato.
 */
const DO_NOT_RESEND: readonly string[] = [
  TICKET_MESSAGE_ERROR_CODES.replyNotRecorded,
  TICKET_MESSAGE_ERROR_CODES.replyInProgress,
];

/** Messaggio dell'errore d'invio: il testo resta nel campo. */
function errorMessage(kind: TicketMessageKind, error: ApiError): string {
  if (error.code === TICKET_MESSAGE_ERROR_CODES.replyNotRecorded) {
    return "L'email è partita ma non è stata registrata nel thread: non reinviarla. Ricarica il ticket più tardi e, se la risposta non compare, segnala il problema a chi gestisce la console.";
  }
  if (error.code === TICKET_MESSAGE_ERROR_CODES.replyInProgress) {
    return `${error.message} Il testo resta qui: non reinviarlo.`;
  }
  return `${kind === 'risposta' ? 'Risposta non inviata' : 'Nota non salvata'}: ${error.message} Il testo è ancora qui.`;
}

/** Una chiave di idempotenza nuova: una per bozza, riusata sui ritentativi dello stesso testo. */
const newDraftKey = (): string => crypto.randomUUID();

/** Esito dell'ultimo invio, da mostrare sotto il pulsante. */
function outcomeMessage(posted: PostedTicketMessage): { severity: 'success' | 'warning'; text: string } {
  const { entry } = posted;
  if (entry.kind === 'nota') return { severity: 'success', text: 'Nota interna aggiunta al thread.' };
  if (posted.stateChangedMeanwhile) {
    return {
      severity: 'warning',
      text: `Risposta inviata a ${entry.recipient}, ma nel frattempo il ticket è passato a «${TICKET_STATE_LABELS[posted.state]}»: lo stato non è cambiato.`,
    };
  }
  return {
    severity: 'success',
    text: `Risposta inviata a ${entry.recipient}. Il ticket è «${TICKET_STATE_LABELS[posted.state]}».`,
  };
}

const COPY: Record<TicketMessageKind, { label: string; placeholder: string; hint: string; submit: string }> = {
  risposta: {
    label: 'Testo della risposta',
    placeholder:
      'Il testo parte come email dalla casella del supporto. Chi ha segnalato lo legge nella sua posta, non in piattaforma.',
    hint: "Testo semplice: gli a capo restano e gli indirizzi web diventano link. Dopo l'invio il ticket passa in attesa dell'utente.",
    submit: 'Invia risposta',
  },
  nota: {
    label: 'Testo della nota interna',
    placeholder: 'Annotazione per il team: diagnosi, cosa hai già verificato, a chi passa la palla.',
    hint: 'Le note interne restano nel ticket e non raggiungono chi ha segnalato. Lo stato non cambia.',
    submit: 'Aggiungi nota',
  },
};

/**
 * Composer sotto il thread (PRD-004 US-4, DEC-008): due modalità, **risposta** via email a chi ha
 * segnalato e **nota interna**; testo semplice, nessuna toolbar. Sul pre-accesso la risposta
 * mostra l'avviso dei recapiti dichiarati con il telefono, se lasciato. Se la risposta non è
 * disponibile (stato o regola della priorità) lo spiega e lascia la nota. Un errore d'invio è
 * mostrato senza perdere il testo; dopo l'invio i dati dei ticket si ricaricano. Gli allegati
 * (#43) fanno parte della bozza: avviso diverso per risposta e nota, limiti verificati prima
 * dell'invio (`ComposerAttachments`), la verifica che conta è dell'API. Sopra il campo, la bozza di
 * risposta preparata via MCP (#159, `ReplyDraftBanner`): «Usa la bozza» la carica come risposta e
 * l'invio, anche «Invia e chiudi», segue il flusso normale; la risposta porta l'`updatedAt` della
 * bozza caricata, così l'API cancella solo quella.
 */
export function TicketComposer({
  ticket,
}: {
  ticket: Pick<TicketDetail, 'code' | 'state' | 'priority' | 'reporter' | 'correction' | 'replyDraft'>;
}) {
  const [kind, setKind] = useState<TicketMessageKind>('risposta');
  const [text, setText] = useState('');
  // «Chiudi il ticket dopo l'invio» (#6): il testo diventa la descrizione della correzione.
  const [closing, setClosing] = useState(false);
  const [release, setRelease] = useState('');
  // Allegati dell'operatore (#43): fanno parte della bozza come il testo.
  const [files, setFiles] = useState<File[]>([]);
  // La bozza è la coppia modalità e testo: cambiandola cambia la chiave, ritentando resta la stessa.
  const [draftKey, setDraftKey] = useState(newDraftKey);
  // Bozza bloccata dopo un'email partita senza registrazione: si sblocca solo cambiando il testo.
  const [lockedKey, setLockedKey] = useState<string | null>(null);
  // `updatedAt` della bozza di risposta caricata (#159): l'invio della risposta lo passa all'API.
  const [loadedReplyDraftAt, setLoadedReplyDraftAt] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const settings = useQueueSettings();
  const post = usePostTicketMessage(ticket.code);
  const resolve = useResolveTicket(ticket.code);
  const isClosing = kind === 'risposta' && closing && canResolve(ticket.state);
  const pending = post.isPending || resolve.isPending;
  const limits = useMessageAttachmentLimits();

  // Senza Impostazioni leggibili la regola la applica comunque l'API (409 mostrato come errore).
  const blocked =
    kind === 'risposta'
      ? (replyBlockedReason(ticket, settings.data) ??
        (isClosing ? resolveBlockedReason(text, ticket.correction, settings.data) : null))
      : null;
  const copy = isClosing ? RESOLVE_COPY : COPY[kind];
  const { reporter } = ticket;
  const length = text.trim().length;
  // Chiudendo il testo è la descrizione della correzione, che ha un limite più basso.
  const maxLength = isClosing ? CORRECTION_TEXT_MAX : TICKET_MESSAGE_TEXT_MAX;
  const tooLong = length > maxLength;
  // «Invia e chiudi» non porta allegati (#43, follow-up): con file scelti l'invio resta bloccato.
  const filesOk = isClosing ? files.length === 0 : attachmentProblems(files, limits).length === 0;
  // Chiudendo il testo può mancare (regola disattivata): l'email dichiara che non ci sono dettagli.
  const canSubmit =
    (length > 0 || isClosing) && !tooLong && filesOk && blocked === null && !pending && lockedKey !== draftKey;
  const outcome = resolve.isSuccess ? resolveOutcome(resolve.data) : post.isSuccess ? outcomeMessage(post.data) : null;

  function changeDraft(next: {
    kind?: TicketMessageKind;
    text?: string;
    closing?: boolean;
    release?: string;
    files?: File[];
  }) {
    if (next.kind !== undefined) setKind(next.kind);
    if (next.text !== undefined) setText(next.text);
    if (next.closing !== undefined) setClosing(next.closing);
    if (next.release !== undefined) setRelease(next.release);
    if (next.files !== undefined) setFiles(next.files);
    setDraftKey(newDraftKey());
  }

  function changeKind(next: TicketMessageKind | null) {
    if (next === null) return;
    changeDraft({ kind: next });
    post.reset();
    resolve.reset();
  }

  /** Spuntando la chiusura con il campo vuoto, la correzione già registrata diventa la bozza. */
  function changeClosing(next: boolean) {
    const prefill = next && text.trim() === '' ? ticket.correction : null;
    changeDraft(prefill ? { closing: next, text: prefill.text, release: prefill.release ?? '' } : { closing: next });
    post.reset();
    resolve.reset();
  }

  /** «Usa la bozza»: il testo della bozza diventa la risposta, modificabile; esiti precedenti via. */
  function loadReplyDraft(draft: TicketReplyDraft) {
    setLoadedReplyDraftAt(draft.updatedAt);
    // Già caricata e invariata: niente chiave nuova né esito azzerato, così un invio bloccato (email
    // partita ma non registrata) resta bloccato e il suo avviso resta.
    if (kind === 'risposta' && text === draft.text) return;
    changeDraft({ kind: 'risposta', text: draft.text });
    post.reset();
    resolve.reset();
  }

  /** Dopo un invio riuscito il composer è vuoto: nessuna bozza è più caricata. */
  function clearAfterSend(next: Parameters<typeof changeDraft>[0]) {
    changeDraft(next);
    setLoadedReplyDraftAt(null);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    const key = draftKey;
    if (isClosing) {
      resolve.mutate(
        { input: { description: text, release }, idempotencyKey: key },
        {
          onSuccess: () => clearAfterSend({ text: '', release: '', closing: false }),
          onError: (error) => {
            if (error.code !== null && DO_NOT_RESEND.includes(error.code)) setLockedKey(key);
          },
        }
      );
      return;
    }
    // La bozza caricata va con la risposta, non con la nota: una nota non tocca la bozza (#158).
    const loadedDraft = kind === 'risposta' && loadedReplyDraftAt ? { replyDraftUpdatedAt: loadedReplyDraftAt } : {};
    post.mutate(
      { message: { kind, text, ...loadedDraft }, idempotencyKey: key, files },
      {
        onSuccess: () => clearAfterSend({ text: '', files: [] }),
        onError: (error) => {
          if (error.code !== null && DO_NOT_RESEND.includes(error.code)) setLockedKey(key);
        },
      }
    );
  }

  return (
    <DetailSection title="Scrivi nel ticket">
      <ReplyDraftBanner
        code={ticket.code}
        draft={ticket.replyDraft}
        composerText={text}
        loaded={ticket.replyDraft !== null && loadedReplyDraftAt === ticket.replyDraft.updatedAt}
        onUse={loadReplyDraft}
        focusComposer={() => textRef.current?.focus()}
      />
      <Stack component="form" spacing={3} noValidate onSubmit={submit}>
        <Stack direction="row" spacing={3} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={kind}
            onChange={(_event, next: TicketMessageKind | null) => changeKind(next)}
            aria-label="Modalità di scrittura"
          >
            <ToggleButton value="risposta">
              <MailOutlineIcon fontSize="small" sx={{ mr: 1 }} />
              Risposta via email
            </ToggleButton>
            <ToggleButton value="nota">
              <LockOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
              Nota interna
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {kind === 'risposta'
              ? `A ${reporter.email} · ${TICKET_RECIPIENT_SOURCE_LABELS[reporter.emailVerified ? 'account' : 'dichiarato']}`
              : `Visibile solo al team · ${INTERNAL_NOTE_MARK}`}
          </Typography>
        </Stack>

        {kind === 'risposta' && !reporter.emailVerified && (
          <Alert severity="warning" role="note" sx={{ overflowWrap: 'anywhere' }}>
            {declaredContactsNotice(reporter.phone)}
          </Alert>
        )}
        {blocked && (
          <Alert severity="info" role="note">
            {blocked}
          </Alert>
        )}

        <TextField
          label={copy.label}
          placeholder={copy.placeholder}
          helperText={copy.hint}
          multiline
          minRows={4}
          value={text}
          onChange={(event) => changeDraft({ text: event.target.value })}
          inputRef={textRef}
          error={tooLong}
        />
        {isClosing && (
          <TextField
            label="Versione di rilascio (facoltativa)"
            placeholder="es. 2.14.3 · in produzione dal 18/09"
            value={release}
            onChange={(event) => changeDraft({ release: event.target.value })}
            slotProps={{ htmlInput: { maxLength: CORRECTION_RELEASE_MAX } }}
          />
        )}
        {isClosing ? (
          files.length > 0 && (
            <Alert
              severity="warning"
              role="note"
              action={
                <Button color="inherit" size="small" onClick={() => changeDraft({ files: [] })}>
                  Togli gli allegati
                </Button>
              }
            >
              «Invia e chiudi» non porta allegati: invia prima una risposta con i file, poi chiudi il ticket.
            </Alert>
          )
        ) : (
          <ComposerAttachments
            kind={kind}
            files={files}
            onChange={(next) => changeDraft({ files: next })}
            limits={limits}
            disabled={pending}
          />
        )}
        {tooLong && (
          <Alert severity="warning" role="note">
            Testo troppo lungo: {length.toLocaleString('it-IT')} caratteri su {maxLength.toLocaleString('it-IT')}.
            Accorcialo di {(length - maxLength).toLocaleString('it-IT')} caratteri per inviarlo.
          </Alert>
        )}

        {post.isError && <Alert severity="error">{errorMessage(kind, post.error)}</Alert>}
        {resolve.isError && <Alert severity="error">{resolveErrorMessage(resolve.error)}</Alert>}
        {outcome && (
          <Alert severity={outcome.severity} role="status">
            {outcome.text}
          </Alert>
        )}

        {/* Sul telefono la casella va a capo prima del pulsante, che non si spezza su due righe (#106). */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center" flexWrap="wrap" useFlexGap>
          {kind === 'risposta' && canResolve(ticket.state) && (
            <FormControlLabel
              control={<Checkbox checked={closing} onChange={(event) => changeClosing(event.target.checked)} />}
              label="Chiudi il ticket dopo l'invio"
            />
          )}
          <Button type="submit" variant="contained" disabled={!canSubmit} sx={{ whiteSpace: 'nowrap' }}>
            {pending ? 'Invio…' : copy.submit}
          </Button>
        </Stack>
      </Stack>
    </DetailSection>
  );
}
